/**
 * MFALoginChallengeModal Component
 *
 * MFA challenge modal for login flow.
 * Displayed after successful password authentication when user has MFA enrolled.
 */

// 1. External dependencies
import React, { useState, useCallback } from 'react';

// 2. Internal absolute imports
import {
  Button,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Select,
  SelectItem
} from '@enfomentis/ui';
import { mfaService } from '@/services/MFAService';
import { useCountdown } from '@/hooks/useCountdown';
import { getTimeSyncLink, getTimeSyncInstructions } from '@/utils/timeSyncLinks';
import type { MFAErrorCode, Factor } from '@/types/mfa';

/**
 * Props for MFALoginChallengeModal
 */
export interface MFALoginChallengeModalProps {
  isOpen: boolean;
  challengeId: string;
  factorId: string;
  expiresAt: number;
  /** All available factors for selection */
  availableFactors: Factor[];
  onVerify: (code: string, factorId: string) => Promise<void>;
  onCancel: () => void;
}

/**
 * MFA Login Challenge Modal Component
 *
 * Features:
 * - TOTP code verification during login
 * - Challenge expiration auto-refresh
 * - Clock skew hints after 3 failures
 * - Rate limiting with countdown
 * - "Lost device" placeholder (for Task 5)
 */
export const MFALoginChallengeModal: React.FC<MFALoginChallengeModalProps> = ({
  isOpen,
  challengeId: initialChallengeId,
  factorId: initialFactorId,
  expiresAt: initialExpiresAt,
  availableFactors,
  onVerify,
  onCancel
}) => {
  // State
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failureCount, setFailureCount] = useState(0);
  const [retryAfter, setRetryAfter] = useState(0);
  const [challengeId, setChallengeId] = useState(initialChallengeId);
  const [factorId, setFactorId] = useState(initialFactorId);
  const [, setExpiresAt] = useState(initialExpiresAt); // Track expiry for future use
  const [isSwitchingFactor, setIsSwitchingFactor] = useState(false);

  // Countdown for rate limiting
  const countdown = useCountdown(retryAfter);

  // Reset form state
  const resetForm = useCallback(() => {
    setCode('');
    setError(null);
    setIsSubmitting(false);
    setFailureCount(0);
    setRetryAfter(0);
  }, []);

  // Handle modal close
  const handleCancel = useCallback(() => {
    resetForm();
    onCancel();
  }, [resetForm, onCancel]);

  // Get context-aware error message
  const getErrorMessage = (errorCode: MFAErrorCode, defaultMessage: string): string => {
    switch (errorCode) {
      case 'INVALID_CODE' as MFAErrorCode:
        return 'Invalid authentication code. Please try again.';
      case 'CLOCK_SKEW' as MFAErrorCode:
        return 'Invalid code. Please check your device time.';
      case 'RATE_LIMITED' as MFAErrorCode:
        return 'Too many attempts. Please wait before trying again.';
      case 'CHALLENGE_EXPIRED' as MFAErrorCode:
        return 'Session expired. Refreshing...';
      case 'NOT_ENROLLED' as MFAErrorCode:
        return 'MFA is not enabled on your account.';
      default:
        return defaultMessage;
    }
  };

  // Auto-refresh expired challenge
  const refreshChallenge = async (): Promise<boolean> => {
    const result = await mfaService.createChallenge(factorId);

    if (!result.success) {
      setError('Failed to create new challenge. Please try logging in again.');
      return false;
    }

    setChallengeId(result.data.challenge_id);
    setExpiresAt(result.data.expires_at);
    setError(null);
    return true;
  };

  // Handle factor switching
  const handleFactorChange = async (newFactorId: string): Promise<void> => {
    if (newFactorId === factorId) return; // No change

    setIsSwitchingFactor(true);
    setError(null);
    setCode(''); // Clear code since it's for different factor

    try {
      // Create new challenge for selected factor
      const result = await mfaService.createChallenge(newFactorId);

      if (!result.success) {
        setError('Failed to switch authenticator. Please try again.');
        setIsSwitchingFactor(false);
        return;
      }

      // Update state with new challenge
      setFactorId(newFactorId);
      setChallengeId(result.data.challenge_id);
      setExpiresAt(result.data.expires_at);
      setFailureCount(0); // Reset failure count
      setRetryAfter(0); // Clear rate limit

      console.log(`✅ Switched to factor: ${newFactorId}`);
    } catch (err) {
      console.error('Factor switch error:', err);
      setError('Failed to switch authenticator. Please try again.');
    } finally {
      setIsSwitchingFactor(false);
    }
  };

  // Handle verification
  const handleVerify = async (): Promise<void> => {
    // Validate code format
    if (!/^\d{6}$/.test(code)) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await mfaService.verifyLogin(challengeId, code, factorId);

      if (!result.success) {
        // Increment failure count
        setFailureCount(prev => prev + 1);

        // Handle challenge expiration with auto-refresh
        if (result.error.error_code === ('CHALLENGE_EXPIRED' as MFAErrorCode)) {
          const refreshed = await refreshChallenge();
          if (refreshed) {
            setError('Your session expired. Please enter your code again.');
          }
          setIsSubmitting(false);
          return;
        }

        // Get error message
        const errorMsg = getErrorMessage(result.error.error_code, result.error.message);
        setError(errorMsg);

        // Handle rate limiting
        if (result.error.retry_after) {
          setRetryAfter(result.error.retry_after);
        }

        setIsSubmitting(false);
        return;
      }

      // Success - call parent verify handler with factorId
      await onVerify(code, factorId);
      resetForm();

    } catch (err) {
      console.error('MFA login verification error:', err);
      setError('Network error. Please check your connection and try again.');
      setIsSubmitting(false);
    }
  };

  // Determine if showing clock skew hint
  const showClockSkewHint = failureCount >= 3 || error?.includes('device time');

  // Log telemetry when clock skew hint is shown
  React.useEffect(() => {
    if (showClockSkewHint && isOpen) {
      console.log('📊 [TELEMETRY] clock_skew_hint_shown', {
        failure_count: failureCount,
        trigger: error?.includes('device time') ? 'error_message' : 'failure_threshold',
        context: 'login_challenge'
      });
    }
  }, [showClockSkewHint, isOpen, failureCount, error]);

  // Get time sync link
  const timeSyncLink = getTimeSyncLink();
  const timeSyncInstructions = getTimeSyncInstructions();

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onCancel}
      size="md"
      placement="center"
      backdrop="blur"
      isDismissable={!isSubmitting}
      isKeyboardDismissDisabled={isSubmitting}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <h3 className="text-xl font-semibold">Two-Factor Authentication</h3>
              <p className="text-sm text-text-secondary font-normal">
                Enter the code from your authenticator app
              </p>
            </ModalHeader>

            <ModalBody>
              <div className="space-y-4">
                {/* Info Alert */}
                <Alert color="primary" variant="flat">
                  <p className="text-sm">
                    MFA is enabled on your account. Please enter your 6-digit verification code to continue.
                  </p>
                </Alert>

                {/* Factor Selector (if multiple factors available) */}
                {availableFactors.length > 1 && (
                  <Select
                    label="Using Authenticator"
                    placeholder="Select authenticator"
                    selectedKeys={[factorId]}
                    onChange={(e) => handleFactorChange(e.target.value)}
                    isDisabled={isSwitchingFactor || isSubmitting}
                    description={isSwitchingFactor ? "Switching authenticator..." : "Select which authenticator to use"}
                  >
                    {availableFactors.map((factor) => (
                      <SelectItem key={factor.id}>
                        {factor.label || factor.friendly_name || 'Authenticator'}
                      </SelectItem>
                    ))}
                  </Select>
                )}

                {/* Verification Code Input */}
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  label="Verification Code"
                  placeholder="000000"
                  value={code}
                  onValueChange={setCode}
                  description="Enter the 6-digit code from your authenticator app"
                  isInvalid={!!error}
                  errorMessage={error || undefined}
                  isDisabled={isSubmitting || countdown > 0}
                  autoFocus
                  classNames={{
                    input: "text-base tracking-wider",
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && code.length === 6) {
                      handleVerify();
                    }
                  }}
                />

                {/* Clock Skew Hint */}
                {showClockSkewHint && (
                  <Alert color="warning" variant="flat">
                    <div className="space-y-2">
                      <p className="font-medium">Your device time may be incorrect</p>
                      <p className="text-sm">{timeSyncInstructions}</p>
                      {timeSyncLink && (
                        <a
                          href={timeSyncLink}
                          className="text-sm text-primary underline hover:text-primary-600"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open Time Settings
                        </a>
                      )}
                    </div>
                  </Alert>
                )}

                {/* Rate Limit Countdown */}
                {countdown > 0 && (
                  <Alert color="danger" variant="flat">
                    <p>Too many attempts. Please try again in {countdown} seconds.</p>
                  </Alert>
                )}

                {/* Lost Device Link */}
                <div className="text-center">
                  <button
                    className="text-sm text-text-secondary hover:text-text-primary underline"
                    onClick={() => {
                      setError('Backup codes are coming in Task 5. Please use your authenticator app for now.');
                    }}
                  >
                    Lost access to your device?
                  </button>
                </div>
              </div>
            </ModalBody>

            <ModalFooter>
              <Button
                color="default"
                variant="light"
                onPress={handleCancel}
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                onPress={handleVerify}
                isLoading={isSubmitting}
                isDisabled={code.length !== 6 || countdown > 0}
              >
                {isSubmitting ? 'Verifying...' : 'Verify and Continue'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
